import { useEffect, useRef, useState } from 'react';
import { fetchPeaks, fetchWindowPeaks, type PeaksSource } from './peaks.ts';
import type { PlacedSegment, Span } from './timeline.ts';

/**
 * Peaks for whatever the working lane is showing, from whichever source suits
 * the width.
 *
 * Two sources, because neither serves both ends of the zoom range:
 *
 * - **Wide** — the stored waveform, 20 points a second. At half an hour across
 *   a screen that is well over a point per pixel, so it is already more detail
 *   than can be drawn, and it costs one cached request per segment.
 * - **Narrow** — audio decoded for exactly the window. At a two-second zoom the
 *   lane wants hundreds of points a second and the stored file cannot supply
 *   them.
 *
 * The crossover is where the stored file stops giving a point per pixel.
 * Decoding a whole block on every zoom-out would take seconds; asking the
 * decoder for a wide window is both slow and pointless.
 */

/** Wait for a drag to settle, so a scrub is one request rather than fifty. */
const DEBOUNCE_MS = 90;
/** Points per second in the stored waveform (PEAKS_PIXELS_PER_SECOND). */
const STORED_POINTS_PER_SECOND = 20;

export function useWindowPeaks(
  placed: PlacedSegment[],
  lane: Span,
  widthPx: number,
): Map<number, PeaksSource> {
  const [sources, setSources] = useState<Map<number, PeaksSource>>(new Map());
  // Stored waveforms, kept between renders: they never change for a segment,
  // and re-fetching them on every zoom would undo the point of using them.
  const storedRef = useRef<Map<number, PeaksSource>>(new Map());
  // So a slow response cannot overwrite a newer one.
  const requestId = useRef(0);

  useEffect(() => {
    if (widthPx <= 0) return;

    const id = requestId.current + 1;
    requestId.current = id;
    const controller = new AbortController();

    const timer = setTimeout(() => {
      const wanted = placed.filter((p) => p.fromMs < lane.toMs && p.toMs > lane.fromMs);
      if (wanted.length === 0) {
        setSources(new Map());
        return;
      }

      const spanSec = (lane.toMs - lane.fromMs) / 1000;
      const storedIsEnough = spanSec * STORED_POINTS_PER_SECOND >= widthPx;

      void Promise.all(
        wanted.map(async (item): Promise<readonly [number, PeaksSource] | null> => {
          if (storedIsEnough) {
            const cached = storedRef.current.get(item.segment.id);
            if (cached) return [item.segment.id, cached] as const;

            const url = item.segment.peaks_url;
            // A segment whose waveform has not been built yet has no wide
            // source; the lane draws it as a flat band rather than nothing.
            if (!url) return null;
            const data = await fetchPeaks(url);
            const source: PeaksSource = { data, fromSec: 0 };
            storedRef.current.set(item.segment.id, source);
            return [item.segment.id, source] as const;
          }

          // Ask only for the part of this segment the lane can see, and for as
          // many points as there are pixels to draw them in.
          const fromMs = Math.max(lane.fromMs, item.fromMs);
          const toMs = Math.min(lane.toMs, item.toMs);
          const fromSec = Math.max(0, (fromMs - item.fileStartMs) / 1000);
          const durationSec = (toMs - fromMs) / 1000;
          const share = (toMs - fromMs) / (lane.toMs - lane.fromMs);
          const pixels = Math.max(8, Math.round(widthPx * share));

          const data = await fetchWindowPeaks(
            item.segment.id, fromSec, durationSec, pixels, controller.signal,
          );
          return [item.segment.id, { data, fromSec }] as const;
        }),
      )
        .then((entries) => {
          if (requestId.current !== id) return; // a newer window won
          setSources(new Map(entries.filter((e): e is readonly [number, PeaksSource] => e !== null)));
        })
        // An aborted or failed read leaves the last window on screen.
        .catch(() => undefined);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [placed, lane.fromMs, lane.toMs, widthPx]);

  return sources;
}
